import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { AuthService } from '../services/auth/auth.service'
import { LoggedUser } from '../models/loggedInUser.model'
import { AuthenticationLoginResponseModel, richiedeSelezioneEnte } from '../models/authenticationLoginResponse.model'
import { MatSnackBar } from '@angular/material/snack-bar'
import { TranslateModule, TranslateService } from '@ngx-translate/core'

/**
 * Component for handling user login functionality.

 * This component provides a login form with email and password fields. Users can enter their credentials and submit the form for authentication. The component interacts with the AuthService to perform login and retrieve user information.

 * @export
 * @class LoginComponent
 * @implements OnInit
 */
@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    imports: [ReactiveFormsModule, TranslateModule],
})
export class LoginComponent implements OnInit {
    /**
     * FormGroup instance representing the login form.
     */
    loginForm: FormGroup
    /**
     * Flag indicating whether to show the loading spinner.
     */
    public showSpinner = false
    /**
     * Login error message displayed to the user.
     */
    public loginMessage: string = ''
    /**
     * Flag to control password visibility.
     */
    public hidePassword: boolean = true

    /**
     * Constructor for the LoginComponent.

     * @param fb {FormBuilder} FormBuilder service to create the login form.
     * @param router {Router} Router service for navigation.
     * @param authService {AuthService} AuthService instance for user authentication.
     * @param _snackBar {MatSnackBar} MatSnackBar service for displaying messages (optional).
     */
    constructor(
        private fb: FormBuilder,
        private router: Router,
        private authService: AuthService,
        private _snackBar: MatSnackBar,
        private routes: ActivatedRoute,
        private translate: TranslateService
    ) {
        this.loginForm = this.fb.group({
            email: ['', [Validators.required]],
            password: ['', Validators.required],
        })
    }

    /**
     * Private method to handle default redirection if a user is already logged in.
     */
    private deftRedirect() {
        this.router.navigate(['/dashboard'])
    }

    /**
     * Lifecycle hook called after component initialization.

     * - Checks for a previously logged-in user information in localStorage and redirects if found.
     */
    ngOnInit() {
          this.routes.params.subscribe((params: Params) => {            
            let codiceAutoLogin: string | null = params['codiceAutoLogin'];
            if (codiceAutoLogin != null && codiceAutoLogin.trim() != "") {
              /* this.loading = true; */
              this.authService.loginAutomatico(codiceAutoLogin);
            }
            else {
                this.checkRedirect()
            }
          });
              
    }

    /**
     * Private method to check for a previously logged-in user in localStorage.

     * - Retrieves the 'user' key from localStorage.
     * - If the value exists and is not empty, redirects to the default route (/dashboard).
     */
    private checkRedirect() {
        const user: string | null = localStorage.getItem('user')
        if (user != null && user != '') {
            this.deftRedirect()
        }
    }

    /**
     * Handles the login form submission.

     * - Performs form validation.
     * - If valid, retrieves form values (username and password).
     * - Shows loading spinner.
     * - Calls AuthService.login(username, password) to perform authentication.
     *   - On success:
     *     - Sets authentication result in AuthService.
     *     - Hides loading spinner.
     *     - Retrieves logged user information using AuthService.getLoggedUserInfo().
     *       - On success:
     *         - Sets user information in AuthService.
     *         - Sets isLoggedIn flag to true.
     *         - Redirects to the default route (/dashboard).
     *       - On error:
     *         - Logs the error to console.
     *   - On error:
     *     - Logs the error to console.
     *     - Hides loading spinner.
     *     - Resets the login form.
     */
    login() {
        if (this.loginForm.valid) {
            const username = this.loginForm.controls['email'].value
            const password = this.loginForm.controls['password'].value
            this.showSpinner = true
            this.loginMessage = ''
            this.authService.login(username, password).subscribe({
                next: (val: any) => {
                    this.authService.setAuthResult(val)
                    this.showSpinner = false

                    if (richiedeSelezioneEnte(val as AuthenticationLoginResponseModel)) {
                        // Login "ponte" (admin/operatore senza ente noto): niente utente locale finché
                        // l'ente non viene scelto, quindi niente getLoggedUserInfo/redirect a dashboard.
                        this.router.navigate(['/seleziona-organizzazione'])
                        return
                    }

                    this.authService.getLoggedUserInfo().subscribe({
                        next: (info) => {
                            this.authService.setUserResult(info as LoggedUser)
                            this.authService.isLoggedIn = true
                            this.deftRedirect()
                        },
                        error: (err) => {
                            // console.log(err)
                        },
                    })
                },
                error: (error) => {
                    this.showSpinner = false
                    this.loginMessage = error?.error?.title || this.translate.instant('LOGIN_ERROR_GENERIC')
                    this.loginForm.reset()
                },
            })
        }
    }

    /**
     * Getter for the email form control.

     * This getter provides access to the email form control and its value.

     * @returns {AbstractControl} The email form control.
     */
    get email() {
        return this.loginForm.get('email')!
    }
}
